import { id } from "../utils";

export * from "./ReportParams/ReportUdfServices";
export * from "./ReportParams/ReportApiServices";

export interface Item {
  id: string;
  name: string;
  displayName?: string; // Category for UDFs
}

export interface ParameterDefinition {
  name: string;
  type: string;
  call: string;
  default: string;
  value?: string;
  required?: boolean;
}

export type ItemsState = Record<string, Item[]>;
export type StringState = Record<string, string>;

// Resolves configured JSON field names with an exact match first, then a case-insensitive match.
export const getFieldValue = (row: unknown, field: string): unknown => {
  if (!row || typeof row !== "object" || !field) return undefined;

  const record = row as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(record, field)) {
    return record[field];
  }

  const normalizedField = field.toLowerCase();
  const matchingKey = Object.keys(record).find((key) => key.toLowerCase() === normalizedField);
  return matchingKey ? record[matchingKey] : undefined;
};

// ============= Parameter Resolution Functions =============

export const allResolved = (
  call: string,
  selectedItem: StringState,
  parameterDefinitions: ParameterDefinition[]
): boolean => {
  let i = 0;
  while (i < call.length) {
    let j = call.indexOf("%(", i);
    if (j < 0) {
      break;
    }
    let k = call.indexOf(")", j);
    if (k < 0) {
      return false;
    }
    let name = call.substring(j + 2, k);
    let nameId = id(name);
    let type = parameterDefinitions.find((p) => p.name == name)?.type;
    if (type != "text" && (selectedItem[nameId]?.length ?? 0) == 0) {
      return false;
    }
    i = k + 1;
  }
  return true;
};

export const fillCall = (
  call: string,
  selectedItem: StringState,
  selectedUdfs: string | null = null,
  items: ItemsState = {}
): string => {
  let i = 0;
  while (i < call.length) {
    let j = call.indexOf("%(", i);
    if (j < 0) {
      break;
    }
    let k = call.indexOf(")", j);
    if (k < 0) {
      break;
    }
    let name = call.substring(j + 2, k);
    let nameId = id(name);
    if (selectedItem[nameId]?.length ?? 0 > 0) {
      let m = call.lastIndexOf("[", j);
      let n = call.indexOf("]", k);
      if (m >= 0 && n >= 0) {
        call = call.substring(0, m) + call.substring(m + 1);
        n--;
        call = call.substring(0, n) + call.substring(n + 1);
        j--;
        k--;
      }
      // replace ampersand with %26 to avoid issues in the call parsing
      let value = selectedItem[nameId].replace(/&/g, "%26");
      if (call[k + 1] == "*") {
        k++;
        value = allSelected(items[nameId], selectedItem[nameId])
          ? "'*'"
          : "'" + value.replace(/, /g, "', '") + "'";
      }
      call = call.substring(0, j) + value + call.substring(k + 1);
      i = j + (value?.length ?? 0);
    } else {
      let m = call.lastIndexOf("[", j);
      let n = call.indexOf("]", k);
      if (m < 0 || n < 0) {
        i = k + 1;
        continue;
      }
      call = call.substring(0, m) + call.substring(n + 1);
      i = m;
    }
  }
  if (selectedUdfs != null) {
    call = call.replace(
      "&udf=*",
      "&udf=" +
        encodeURIComponent(selectedUdfs.replace(/, /g, ",")).replace(/%2C/g, ",")
    );
  }
  return call;
};

export const dependency = (call: string, changedParameter: string): boolean => {
  // Placeholders in calls use display names (e.g. %(O Unit Id)) while
  // changedParameter is tracked as id(name) (e.g. oUnitId).
  // Normalize placeholder names so dependency checks work for both forms.
  let i = 0;
  while (i < call.length) {
    const j = call.indexOf("%(", i);
    if (j < 0) {
      break;
    }
    const k = call.indexOf(")", j);
    if (k < 0) {
      return false;
    }

    const placeholderName = call.substring(j + 2, k);
    if (id(placeholderName) === changedParameter || placeholderName === changedParameter) {
      return true;
    }

    i = k + 1;
  }

  return false;
};

export const allUsesOptional = (
  name: string,
  parameterDefinitions: ParameterDefinition[]
): boolean => {
  for (let parameter of parameterDefinitions) {
    let call = parameter.call;
    let usageName = `%(${name})`;
    if (!call.includes(usageName)) {
      continue;
    }
    let i = 0;
    while (i < call.length) {
      let j = call.indexOf(usageName, i);
      if (j < 0) {
        break;
      }
      let k = call.lastIndexOf("[", j);
      if (k < 0) {
        return false;
      }
      let l = call.indexOf("]", k);
      if (l < 0 || l < j) {
        return false;
      }
      i = j + usageName.length;
    }
  }
  return true;
};

export const allUnselectedParamsAreOptional = (
  call: string,
  selectedItem: StringState
): boolean => {
  let i = 0;
  while (i < call.length) {
    let j = call.indexOf("%(", i);
    if (j < 0) {
      break;
    }
    let k = call.indexOf(")", j);
    if (k < 0) {
      return false;
    }
    let paramName = call.substring(j + 2, k);
    let nameId = id(paramName);
    
    // Check if this parameter is selected
    const isSelected = (selectedItem[nameId]?.length ?? 0) > 0;
    
    if (!isSelected) {
      // Parameter is not selected, check if it's optional
      let m = call.lastIndexOf("[", j);
      let n = call.indexOf("]", k);
      // If not wrapped in brackets, it's required
      if (m < 0 || n < 0 || n < j) {
        return false;
      }
    }
    i = k + 1;
  }
  return true;
};

// ============= Drilldown Call Functions =============

export const fillDrilldownCall = (
  call: string,
  row: any,
  selectedItem?: StringState
): string => {
  let filledCall = call;
  let i = 0;
  while (i < filledCall.length) {
    let j = filledCall.indexOf("%(", i);
    if (j < 0) {
      break;
    }
    let k = filledCall.indexOf(")", j);
    if (k < 0) {
      break;
    }
    let name = filledCall.substring(j + 2, k);
    let value = String(getFieldValue(row, name) ?? selectedItem?.[name] ?? "");
    if (filledCall[k + 1] == "*") {
      k++;
      value = "'" + value.replace(/, /g, "', '") + "'";
    }

    filledCall = filledCall.substring(0, j) + value + filledCall.substring(k + 1);
    i = j + (value?.length ?? 0);
  }
  return encodeURIComponent(filledCall);
};

// ============= Selection Management Functions =============

// `allTargetItems` decides whether the "all" sentinel is included - the full item list normally,
// or just the filtered subset while a search query is active, so the "All (filtered)" checkbox
// reflects whether every currently filtered item is selected rather than every item overall.
export const getSelectedOptions = (items: Item[], selectedItem: string, allTargetItems: Item[] = items): string[] => {
  // A set, not an array scan: both lists can hold six figures worth of ids for parameters such as jobs.
  const selected = new Set(selectedItem?.split(", ") ?? []);
  let selectedItems = items?.filter((item) => selected.has(item.id)).map((item) => item.id) ?? [];
  const targetSelectedCount = allTargetItems?.filter((item) => selected.has(item.id)).length ?? 0;
  if (targetSelectedCount > 0 && targetSelectedCount == (allTargetItems?.length ?? 0)) {
    selectedItems.push("all");
  }
  return selectedItems;
};

// `items` is the target set for the toggle - the full list normally, but only the currently
// filtered subset when a search query is active, so "select all" while filtering only touches
// what is visible and leaves selections outside the filter untouched.
export const handleSelectAll = (
  items: Item[],
  selectedValue: string,
  onSetSelected: (value: string) => void
): void => {
  const selectedIds = new Set(selectedValue ? selectedValue.split(", ") : []);
  const isTargetFullySelected = (items?.length ?? 0) > 0 && items.every((item) => selectedIds.has(item.id));
  if (isTargetFullySelected) {
    items?.forEach((item) => selectedIds.delete(item.id)); // Deselect the target items
  } else {
    items?.forEach((item) => selectedIds.add(item.id)); // Select the target items
  }
  onSetSelected(Array.from(selectedIds).join(", "));
};

export const handleOptionSelect = (
  items: Item[],
  selectedValue: string,
  onSetSelected: (value: string) => void,
  data: any
): void => {
  if (data.optionValue === "all") {
    handleSelectAll(items, selectedValue, onSetSelected); // If "Select All" is clicked
  } else {
    onSetSelected(data.selectedOptions?.join(", ") || ""); // Update selected options
  }
};

export const allSelected = (items: Item[], selectedItem: string): boolean => {
  const selectedIds = new Set(selectedItem?.split(", ") ?? []);
  return (items?.length ?? 0) > 0 && items.every((item) => selectedIds.has(item.id));
};

